import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslations } from 'next-intl';

import track from '@helper/tracking';

import {
  FacebookBorderIcon,
  MailIcon,
  WhatsAppIcon,
  CopyIcon,
  LinkedInIcon,
  ThreadsIcon
} from '../../icons/sharing/sharing';
import Link from '../../atoms/link/link';
import Notification from '../../atoms/notification/notification';
import StyledShareBox from './shareBox.style';

const ShareBox = ({ seo, staticHor = false, ...props }) => {
  const t = useTranslations();
  const [copied, setCopied] = useState(false);
  const copyLink = useRef();

  const copyToClipboard = () => {
    setCopied(true);
    navigator.clipboard.writeText(seo.links.canonical);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    copyLink.current.addEventListener('click', copyToClipboard);
    copyLink.current.addEventListener('keydown', e => (e.key === 'Enter' ? copyToClipboard() : ''));

    return () => {
      if (!copyLink?.current) return;
      copyLink.current.removeEventListener('click', copyToClipboard);
      copyLink.current.removeEventListener('keydown', e =>
        e.key === 'Enter' ? copyToClipboard() : ''
      );
    };
  });

  const handleTracking = type => {
    track(['trackEvent', 'Sharing', 'Click', type]);
  };

  return (
    <StyledShareBox staticHor={staticHor} {...props}>
      <div className="sharebox__wrapper">
        <Link
          role="button"
          tabIndex="0"
          ref={copyLink}
          aria-label={t('share.copy')}
          onClick={() => handleTracking('Copy Link')}
        >
          <CopyIcon />
        </Link>

        <Link
          href={`https://www.facebook.com/sharer.php?t=${seo?.og?.title}&u=${seo?.links?.canonical}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('share.facebook')}
          onClick={() => handleTracking('Facebook')}
        >
          <FacebookBorderIcon />
        </Link>

        <Link
          href={`https://wa.me/?text=${seo?.og?.title || seo?.title} - Futurium ${
            seo?.links?.canonical
          }`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('share.whatsapp')}
          onClick={() => handleTracking('WhatsApp')}
        >
          <WhatsAppIcon />
        </Link>

        <Link
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${
            seo?.og?.title || seo?.title
          } - Futurium ${seo?.links?.canonical}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('share.linkedIn')}
          onClick={() => handleTracking('LinkedIn')}
        >
          <LinkedInIcon />
        </Link>

        <Link
          href={`https://threads.net/intent/post?text=${seo?.og?.title || seo?.title} - Futurium ${
            seo?.links?.canonical
          }`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('share.threads')}
          onClick={() => handleTracking('Threads')}
        >
          <ThreadsIcon />
        </Link>

        <Link
          href={`mailto:?subject=${seo?.og?.title} – Futurium&body=${seo?.links?.canonical}`}
          aria-label={t('share.mail')}
          onClick={() => handleTracking('Mail')}
        >
          <MailIcon />
        </Link>
      </div>
      {copied && <Notification>{t('link.copied')}</Notification>}
    </StyledShareBox>
  );
};

ShareBox.propTypes = {
  seo: PropTypes.shape().isRequired,
  staticHor: PropTypes.bool
};

const mapStateToProps = state => ({
  seo: state.app.pageData.seo
});

export default connect(mapStateToProps)(ShareBox);
